import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Calculator, TrendingUp, Download, Share2, BookOpen } from 'lucide-react';
import { Badge } from '../ui/badge';
import jsPDF from 'jspdf';

const URSSAFSimulator = () => {
  const [inputs, setInputs] = useState({
    revenu: '50000',
    statut: 'tns',
    activite: 'services'
  });
  const [results, setResults] = useState({});

  const calculate = () => {
    const revenu = parseFloat(inputs.revenu) || 0;
    const PASS = 46368;

    if (inputs.statut === 'tns') {
      const maladie = revenu * 0.065;
      const retraiteBase = Math.min(revenu, PASS) * 0.1775;
      const retraiteCompl = revenu * 0.07;
      const allocFamiliales = revenu > PASS * 1.1 ? revenu * 0.031 : revenu * 0.0;
      const csgCrds = revenu * 0.097;
      const invaliditeDeces = Math.min(revenu, PASS) * 0.013;
      const total = maladie + retraiteBase + retraiteCompl + allocFamiliales + csgCrds + invaliditeDeces;

      setResults({
        maladie: Math.round(maladie),
        retraiteBase: Math.round(retraiteBase),
        retraiteCompl: Math.round(retraiteCompl),
        allocFamiliales: Math.round(allocFamiliales),
        csgCrds: Math.round(csgCrds),
        invaliditeDeces: Math.round(invaliditeDeces),
        total: Math.round(total),
        revenuNet: Math.round(revenu - total),
        taux: revenu > 0 ? (total / revenu * 100).toFixed(1) : '0'
      });
    } else {
      const chargesPatronales = revenu * 0.42;
      const chargesSalariales = revenu * 0.22;
      const total = chargesPatronales + chargesSalariales;
      const coutTotal = revenu + chargesPatronales;

      setResults({
        chargesPatronales: Math.round(chargesPatronales),
        chargesSalariales: Math.round(chargesSalariales),
        total: Math.round(total),
        revenuNet: Math.round(revenu - chargesSalariales),
        coutTotal: Math.round(coutTotal),
        taux: revenu > 0 ? (total / revenu * 100).toFixed(1) : '0'
      });
    }
  };

  useEffect(() => { calculate(); }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) { navigator.share({ title: 'Simulateur URSSAF - Bozzam', url }); }
    else { navigator.clipboard.writeText(url); alert('Lien copié !'); }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFillColor(30, 64, 175); doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255); doc.setFontSize(20);
    doc.text('Bozzam - Charges URSSAF', 15, 25);
    doc.setFontSize(10); doc.text('blog.bozzam.com', 15, 35);
    doc.setTextColor(0); let y = 55;
    doc.setFontSize(14); doc.text('Résultats', 15, y); y += 10;
    doc.setFontSize(11);
    doc.text(`Revenu / Salaire brut : ${parseInt(inputs.revenu).toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Statut : ${inputs.statut === 'tns' ? 'TNS (indépendant)' : 'Assimilé salarié'}`, 15, y); y += 7;
    doc.text(`Total cotisations : ${results.total?.toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Taux global : ${results.taux}%`, 15, y); y += 7;
    doc.text(`Revenu net : ${results.revenuNet?.toLocaleString()} EUR`, 15, y); y += 15;
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text('Sources : URSSAF 2026, CSS, PASS 46 368 EUR', 15, y);
    doc.save('simulation-urssaf-bozzam.pdf');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" /> Estimation URSSAF
            </CardTitle>
            <CardDescription>Estimez vos cotisations URSSAF 2026</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>{inputs.statut === 'tns' ? 'Revenu professionnel annuel (€)' : 'Salaire brut annuel (€)'}</Label>
              <Input data-testid="revenu-input" type="text" inputMode="numeric" value={inputs.revenu}
                onChange={(e) => handleInputChange('revenu', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="50000" className="mt-1" />
            </div>
            <div>
              <Label>Statut</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Button data-testid="statut-tns-btn"
                  variant={inputs.statut === 'tns' ? 'default' : 'outline'}
                  className={inputs.statut === 'tns' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  onClick={() => handleInputChange('statut', 'tns')}>TNS (indépendant)</Button>
                <Button data-testid="statut-assimile-btn"
                  variant={inputs.statut === 'assimile' ? 'default' : 'outline'}
                  className={inputs.statut === 'assimile' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  onClick={() => handleInputChange('statut', 'assimile')}>Assimilé salarié</Button>
              </div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <BookOpen className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sources légales 2026</p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>URSSAF - urssaf.fr - Barèmes cotisations 2026</li>
                    <li>PASS 2026 : 46 368€</li>
                    <li>Code de la Sécurité Sociale, art. L131-6 et suivants</li>
                    <li>Taux CSG-CRDS : 9.7% (dont 6.8% déductible)</li>
                    <li>Assimilé salarié : ~42% patronales + ~22% salariales</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" /> Résultats
            </CardTitle>
            <div className="flex gap-2">
              <Badge className="bg-blue-100 text-blue-800">{inputs.statut === 'tns' ? 'TNS' : 'Assimilé salarié'}</Badge>
              <Badge className="bg-green-100 text-green-800">Taux : {results.taux}%</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {inputs.statut === 'tns' ? (
              <div className="space-y-2">
                {[
                  ['Maladie-maternité', results.maladie],
                  ['Retraite de base', results.retraiteBase],
                  ['Retraite complémentaire', results.retraiteCompl],
                  ['Allocations familiales', results.allocFamiliales],
                  ['CSG-CRDS', results.csgCrds],
                  ['Invalidité-décès', results.invaliditeDeces],
                ].map(([label, val]) => (
                  <div key={label} className="flex justify-between p-3 bg-red-50 rounded">
                    <span className="text-sm text-red-700">{label}</span>
                    <span className="text-sm font-bold text-red-900">{val?.toLocaleString()}€</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex justify-between p-3 bg-red-50 rounded">
                  <span className="text-sm text-red-700">Charges patronales (~42%)</span>
                  <span className="text-sm font-bold text-red-900">{results.chargesPatronales?.toLocaleString()}€</span>
                </div>
                <div className="flex justify-between p-3 bg-red-50 rounded">
                  <span className="text-sm text-red-700">Charges salariales (~22%)</span>
                  <span className="text-sm font-bold text-red-900">{results.chargesSalariales?.toLocaleString()}€</span>
                </div>
              </div>
            )}

            <div className="flex justify-between p-3 bg-red-100 rounded border-2 border-red-200">
              <span className="text-sm font-bold text-red-800">Total cotisations</span>
              <span className="text-sm font-bold text-red-900" data-testid="result-total">{results.total?.toLocaleString()}€</span>
            </div>

            <div className="p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-200 text-center">
              <p className="text-sm text-gray-600 mb-1">Revenu net après cotisations</p>
              <p className="text-3xl font-black text-green-900" data-testid="result-net">{results.revenuNet?.toLocaleString()}€</p>
              <p className="text-sm text-gray-500">{Math.round(results.revenuNet / 12)?.toLocaleString()}€/mois</p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <Button onClick={shareLink} variant="outline" data-testid="share-btn"><Share2 className="h-4 w-4 mr-2" /> Partager</Button>
              <Button onClick={exportPDF} variant="outline" data-testid="export-btn"><Download className="h-4 w-4 mr-2" /> PDF</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}>Bozzam App</Button>
            </div>
            <div className="text-xs text-gray-500 text-center pt-2 border-t">
              <p>Sources : URSSAF 2026 - Code de la Sécurité Sociale</p>
              <p>urssaf.fr | PASS 46 368€ | Barèmes officiels 2026</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default URSSAFSimulator;
